import type { ReactNode } from 'react';
import type { Analytics, EnvironmentData, ExternalLogger } from '@cld/unicorn-api';
import { ServerError } from '@cld/empty-state';

import { useStateManagement } from './state/useStateManagement';
import type { ConsoleLogger, CookieApi, GetAuthenticationToken, LocationApi, StorageClient, SystemTheme } from './state/types';
import type { UnicornApiState } from './state/selectors/selectUnicornApi';
import { DevModeTools } from './ui/ProductBar/DevModeTools';
import type { AbTestingClientFactory } from './api/createAbTestingClient';
import type { ExternalLoggerFactory } from './api/createExternalLoggerFactory';
import { ThemeProvider } from 'styled-components';

export interface UnicornWrapperProps {
  analytics: Analytics;
  abTestingClientFactory: AbTestingClientFactory;
  children: (unicornApi: UnicornApiState) => ReactNode;
  devMode: boolean;
  environmentData: EnvironmentData;
  setForceSplitError: (newValue: boolean) => void;
  forceSplitError: boolean;
  getAuthenticationToken: GetAuthenticationToken;
  consoleLogger: ConsoleLogger;
  unicornExternalLogger: ExternalLogger;
  externalLoggerFactory: ExternalLoggerFactory;
  cookieApi: CookieApi;
  locationApi: LocationApi;
  localStorageClient: Storage;
  enableTheming: boolean;
  systemTheme: SystemTheme;
  unicornStorageClient: StorageClient;
  // navigationContent: ReactNode;
}

export const UnicornWrapper = ({
  analytics,
  abTestingClientFactory,
  devMode,
  children,
  environmentData,
  getAuthenticationToken,
  consoleLogger,
  unicornExternalLogger,
  externalLoggerFactory,
  forceSplitError,
  setForceSplitError,
  cookieApi,
  localStorageClient,
  systemTheme,
  enableTheming,
  locationApi,
  unicornStorageClient,
}: UnicornWrapperProps) => {
  const { dispatch, state, unicornApi, selectedTheme } = useStateManagement({
    analytics,
    abTestingClientFactory,
    devMode,
    environmentData,
    getAuthenticationToken,
    consoleLogger,
    unicornExternalLogger,
    externalLoggerFactory,
    cookieApi,
    localStorageClient,
    systemTheme,
    enableTheming,
    locationApi,
    unicornStorageClient,
  });

  return (
    <>
      {devMode && (
        <DevModeTools
          environmentData={environmentData}
          enabledLoggerCategories={consoleLogger.enabledCategories}
          onLoggerCategoryToggle={consoleLogger.toggleCategory}
          forceSplitError={forceSplitError}
          setForceSplitError={setForceSplitError}
        />
      )}

      {/* FIXME: sync theme to local storage, to instantly restore last used and prevent flash from light to dark */}
      {/* FIXME: show loading spinner (needs some thinking when, probably only on startup and not when unicornApi prepares, so other apps can start loading) */}
      <ThemeProvider theme={selectedTheme}>{state.applicationStatus === 'startupError' ? <ServerError /> : children(unicornApi)}</ThemeProvider>
    </>
  );

  // return (
  //   <Layout>
  //     <Layout.ProductBar>{state.mainLogo.status === 'ready' && <img alt="" src={state.mainLogo.url} />}</Layout.ProductBar>
  //     <Layout.NavigationBar>{navigationContent}</Layout.NavigationBar>
  //     <Layout.ApplicationContent>Application content</Layout.ApplicationContent>
  //   </Layout>
  // );
};
